import { useEffect, useRef, useState, type RefObject } from 'react'
import { WIDE_QUERY } from '@/hooks/useMediaQuery'

/** How far (px, after resistance) the page must be pulled before letting go refreshes. */
export const PULL_THRESHOLD = 70
const MAX_PULL = 110
// Without this a fast answer makes the spinner and fade blink rather than read as a refresh.
const MIN_REFRESH_MS = 500

/** Finger travel → how far the page moves: halved, so it feels like pulling against a spring. */
export function pullDistance(fingerDy: number): number {
  return Math.max(0, Math.min(fingerDy * 0.5, MAX_PULL))
}

/** Pull-down-to-refresh on a scroll container, phones only. Starts only when the container is
 *  already at the top; sheets and dialogs are portals outside it, so they never trigger it. */
export function usePullToRefresh(
  scrollRef: RefObject<HTMLElement | null>,
  onRefresh: () => Promise<unknown>
): { pull: number; refreshing: boolean } {
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const onRefreshRef = useRef(onRefresh)
  useEffect(() => {
    onRefreshRef.current = onRefresh
  }, [onRefresh])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let startY: number | null = null
    let current = 0
    let busy = false

    function onStart(e: TouchEvent) {
      if (busy || e.touches.length !== 1 || el!.scrollTop > 0) return
      if (window.matchMedia?.(WIDE_QUERY).matches) return
      startY = e.touches[0].clientY
    }

    function onMove(e: TouchEvent) {
      if (startY === null) return
      const dy = e.touches[0].clientY - startY
      if (dy <= 0 || el!.scrollTop > 0) {
        startY = null
        current = 0
        setPull(0)
        return
      }
      // Ours replaces the browser's own pull (a full page reload) and iOS's rubber-band bounce.
      if (e.cancelable) e.preventDefault()
      const next = pullDistance(dy)
      if (current < PULL_THRESHOLD && next >= PULL_THRESHOLD) navigator.vibrate?.(10)
      current = next
      setPull(next)
    }

    async function onEnd(e: TouchEvent) {
      if (startY === null) return
      startY = null
      const reached = e.type === 'touchend' && current >= PULL_THRESHOLD
      current = 0
      setPull(0)
      if (!reached) return
      busy = true
      setRefreshing(true)
      try {
        await Promise.allSettled([
          onRefreshRef.current(),
          new Promise((r) => setTimeout(r, MIN_REFRESH_MS)),
        ])
      } finally {
        busy = false
        setRefreshing(false)
      }
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [scrollRef])

  return { pull, refreshing }
}
